import React, { useMemo, useRef } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Tag } from "primereact/tag";
import { formatDateTime } from "../../../../algorithms/date";
import GlobalSelect from "../../../../components/core/global-select";
import TotalRecords from "../../../../components/core/total-records";
import { DownloadButton } from "../../../../components/core";

// ── Status config ────────────────────────────────────────────────────────────
const STATUS_CONFIG = [
  { label: "Pending", tailwind: "bg-blue-500" },
  { label: "Overstay", tailwind: "bg-yellow-500" },
  { label: "Claimed", tailwind: "bg-emerald-500" },
  { label: "Removed", tailwind: "bg-slate-500" },
  { label: "Cancelled", tailwind: "bg-slate-500" },
  { label: "Take-Back", tailwind: "bg-orange-500" },
  { label: "For RTS", tailwind: "bg-orange-500" },
  { label: "Revoked", tailwind: "bg-red-700" },
  { label: "RTS Overstay", tailwind: "bg-red-500" },
  { label: "RTS", tailwind: "bg-red-700" },
  { label: "Cleared Out", tailwind: "bg-purple-500" },
];

const packageStatusHelper = (label) => {
  const found = STATUS_CONFIG.find((s) => s.label === label);
  return found
    ? `${found.tailwind} text-center border-0 text-white`
    : "border-0 text-center text-white bg-gray-400";
};

const PACKAGE_STATES_FLAT = {
  110: "Pending",
  118: "Removed",
  121: "Overstay",
  128: "Claimed",
  130: "Take-Back",
  138: "Revoked",
  140: "For RTS",
  141: "RTS Overstay",
  148: "RTS",
  150: "Cancelled",
  158: "Cleared Out",
};

const headerClassName = "bg-primary-primaryDarkGray text-primary-primaryYellow";

const PackageStatusTable = ({ items = [], searchResultHeader = "" }) => {
  const dt = useRef(null);

  const mappedData = useMemo(() => {
    if (!items || items.length === 0) return [];
    return items.map((item) => ({
      ...item,
      packageStateLabel: PACKAGE_STATES_FLAT[item.packageStateNo] ?? "Unknown",
      hasIntegration: item.partnerBusinessPartnerId != null,
    }));
  }, [items]);

  const uniqueStatusOptions = useMemo(() => {
    const seen = new Set();
    return Object.values(PACKAGE_STATES_FLAT).reduce((acc, label) => {
      if (!seen.has(label)) {
        seen.add(label);
        acc.push({ value: label, label });
      }
      return acc;
    }, []);
  }, []);

  const packageStatusFilterTemplate = (options) => (
    <GlobalSelect
      value={options.value ?? null}
      items={uniqueStatusOptions}
      onChange={(e) => {
        const value = e.target.value;
        options.filterCallback(value === "" || value === null ? null : value);
      }}
      placeholder="Select a Status"
      className="p-column-filter"
      showClear
    />
  );

  const exportColumns = [
    { field: "packageStateLabel", header: "Package Status" },
    { field: "lockerLocation", header: "Locker Location" },
    { field: "partnerAgentMobile", header: "Courier Mobile Number" },
    { field: "waybillNumber", header: "Waybill Number" },
    { field: "packageDate", header: "Package Dropoff Date" },
  ];

  return (
    <>
      <div className="text-center pt-3 bg-primary-primaryDarkGray text-primary-primaryYellow p-2">
        <h1 className="font-bold">PACKAGE STATUS</h1>
        <h1 className="font-bold">{searchResultHeader}</h1>
      </div>

      <div className="grid grid-cols-2 items-center w-full">
        <TotalRecords
          count={mappedData?.length}
          styleClassname={`${headerClassName} col-span-1`}
        />
        <div className="flex justify-end bg-primary-primaryDarkGray p-2">
          <DownloadButton
            id="package-status-table"
            exportFilename="package-status"
            data={mappedData}
            columns={exportColumns}
            dt={dt}
          />
        </div>
      </div>

      <div id="package-status-table">
        <DataTable
          ref={dt}
          value={mappedData}
          size="large"
          rows={mappedData?.length}
          emptyMessage="No packages found"
          cellSelection
          onCellClick={(e) => {
            switch (e.field) {
              case "waybillNumber":
                if (e.rowData.packageId) {
                  window.open(
                    `/view-package-history/${e.rowData.packageId}/${e.rowData.userAccount?.userAccountId}`,
                    "_blank",
                  );
                }
                break;
              case "partnerAgentMobile":
                if (e.rowData.partnerAgentId) {
                  window.open(`/agents/${e.rowData.partnerAgentId}`, "_blank");
                }
                break;
              default:
                break;
            }
          }}
          scrollable
          scrollHeight="600px"
        >
          <Column
            header="Package Status"
            filter
            showFilterMatchModes={false}
            showFilterMenuOptions={false}
            showAddButton={false}
            filterElement={packageStatusFilterTemplate}
            filterMatchMode="equals"
            filterField="packageStateLabel"
            headerClassName={headerClassName}
            alignHeader="center"
            body={(rowData) => (
              <div className="flex flex-col items-center gap-1">
                <Tag className={packageStatusHelper(rowData.packageStateLabel)}>
                  {rowData.packageStateLabel}
                </Tag>
                {rowData.hasIntegration && (
                  <span className="text-[9px] font-bold bg-sky-100 text-sky-700 border border-sky-300 px-1.5 py-0.5 rounded-full whitespace-nowrap">
                    Integrated
                  </span>
                )}
              </div>
            )}
            className="text-center p-4"
          />
          <Column
            field="lockerLocation"
            header="Locker Location"
            headerClassName={headerClassName}
            alignHeader="center"
            className="text-center p-2"
          />
          <Column
            field="partnerAgentMobile"
            header="Courier Mobile Number"
            headerClassName={headerClassName}
            alignHeader="center"
            className="text-center cursor-pointer hover:text-blue-500"
          />
          <Column
            field="waybillNumber"
            header="Waybill Number"
            sortable
            headerClassName={headerClassName}
            alignHeader="center"
            className="text-center cursor-pointer hover:text-blue-500"
          />
          <Column
            field="packageDate"
            header="Package Dropoff Date"
            sortable
            headerClassName={headerClassName}
            alignHeader="center"
            className="text-center p-2"
            body={(rowData) => <div>{formatDateTime(rowData.packageDate)}</div>}
          />
        </DataTable>
      </div>
    </>
  );
};

export default PackageStatusTable;
